'use client';

// The "plus VAT" qualifier that sits beside a listed price for EU buyers.
//
// See src/lib/vat-notice.ts for why this exists at all (the Price Indication
// Directive wants a consumer-facing price to be VAT-inclusive, and ours is not)
// and for why the country list is the EU-27 and nothing else.
//
// Renders NOTHING outside that scope, so every call site can drop it in beside a
// price unconditionally rather than repeating the country test. Returning null is
// the overwhelmingly common path: most visitors are not in the EU.

import { useGeoCountry } from '@/context/geo-context';
import { showsVatNotice, VAT_NOTICE_LABEL } from '@/lib/vat-notice';
import { cn } from '@/lib/utils';

export function VatNotice({
  /**
   * The price the qualifier is attached to. A free product, a $0 tier and a
   * pay-what-you-want minimum of nothing all charge no VAT, so qualifying them
   * would state a cost that does not exist. Omit only where the call site has
   * already established the price is positive.
   */
  amount,
  className,
}: {
  amount?: number | null;
  className?: string;
}) {
  const country = useGeoCountry();
  if (!showsVatNotice(country)) return null;
  // `amount === undefined` means the caller vouched for it; an explicit null or a
  // non-positive number means there is nothing to tax.
  if (amount !== undefined && !(Number(amount) > 0)) return null;
  return (
    <span
      className={cn('text-xs font-normal text-muted-foreground', className)}
      // The visible text is deliberately terse enough to fit beside a price on a
      // crowded card, so the full sentence lives in the tooltip/description for
      // anyone who hovers or uses a screen reader.
      title="VAT is added at checkout and shown before you pay. The rate depends on your country."
    >
      {VAT_NOTICE_LABEL}
    </span>
  );
}
